import PropTypes from 'prop-types';
import React, { useEffect, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { Button, SelectField, validations } from '@unite-us/app-components';
import { validateReduxForm } from 'common/form';
import Notifier from 'common/helpers/Notifier';
import { updateServiceCase } from 'actions/Case/Contact/Group';
import usePersonConditions from 'src/common/hooks/usePersonConditions';
import { OverlaySpinner } from 'common/spinners';
import { coreApi } from 'src/api/config';
import { get } from 'lodash';
import './stylesheets/detail-view.scss';

const fields = ['client_need'];

const createNeedChangeNote = (referralId, employeeId, previousTitle, updatedTitle) => ({
  subjectType: 'referral',
  subject: referralId,
  employee: employeeId,
  visibility: 'public',
  category: 'general',
  details: { deletable: false, type: 'update' },
  text: `Associated need changed from ${previousTitle} to ${updatedTitle}.`,
});

const useGroupedReferrals = () => useCallback(async (caseId) => {
  try {
    const response = await coreApi.query('referral', { case: caseId });
    return response.data?.data || [];
  } catch (error) {
    return [];
  }
}, []);

const useNeedChangeNotes = (fetchGroupedReferrals) => useCallback(async (
  currentReferral,
  employee,
  previousTitle,
  updatedTitle,
) => {
  if (!employee || previousTitle === updatedTitle) return;

  try {
    const referralsToUpdate = currentReferral.is_grouped ?
      await fetchGroupedReferrals(currentReferral.case.id) :
      [currentReferral];

    const notePromises = referralsToUpdate.map((r) => coreApi.createRecord(
      'note',
      createNeedChangeNote(r.id, employee.id, previousTitle, updatedTitle),
    ));
    await Promise.all(notePromises);
  } catch (error) {
    Notifier.dispatch('error', 'Error creating note for associated need update');
    throw error;
  }
}, [fetchGroupedReferrals]);

const ClientNeedEditForm = ({
  needId,
  initializeForm,
  referral,
  closeModal,
  clientNeeds,
  fields: { client_need },
  handleSubmit,
  submitting,
  registerField,
  onUpdate,
  currentEmployee,
  personId,
}) => {
  const queryClient = useQueryClient();
  const fetchGroupedReferrals = useGroupedReferrals();
  const addNeedChangeNote = useNeedChangeNotes(fetchGroupedReferrals);
  const clientNeedsFiltered = usePersonConditions(personId, true);

  useEffect(() => {
    initializeForm({ client_need: needId, referral });
  }, [needId]);

  const onSubmit = useCallback(async (formValues) => {
    const updatedNeedId = formValues.client_need.id;
    const previousNeed = clientNeeds.find((need) => need.id === needId);
    const updatedNeed = clientNeedsFiltered.find((need) => need.id === updatedNeedId);

    try {
      await coreApi.updateRecord('case', referral.case.id, { client_need: updatedNeedId });
      await addNeedChangeNote(referral, currentEmployee, previousNeed?.name, updatedNeed?.name);

      queryClient.invalidateQueries(['notes']);

      if (onUpdate) {
        onUpdate(updatedNeedId);
      }
      closeModal();
    } catch (error) {
      Notifier.dispatch('error', 'Error updating associated need');
      throw error;
    }
  }, [referral.case.id, closeModal, onUpdate, addNeedChangeNote, clientNeeds, needId]);

  return (
    <form className="content-with-actions" onSubmit={handleSubmit(onSubmit)}>
      <OverlaySpinner show={submitting} text="Submitting Client Need..." />
      <div className="mb-20">
        <SelectField
          ref={registerField}
          field={client_need}
          id="client_need"
          data-testid="client_need"
          label="Associated Need"
          placeholder="Choose one..."
          validations={validations.isRequired}
          required
          options={clientNeedsFiltered}
          labelKey="name"
          valueKey="id"
        />
      </div>
      <div className="actions">
        <span className="action-item">
          <Button
            id="client-need-cancel-btn"
            label="Cancel"
            onClick={closeModal}
            disabled={submitting}
          />
        </span>
        <span className="action-item">
          <Button
            id="client-need-submit-btn"
            onClick={handleSubmit(onSubmit)}
            primary
            disabled={submitting}
            label="Submit"
          />
        </span>
      </div>
    </form>
  );
};

ClientNeedEditForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
  needId: PropTypes.string.isRequired,
  clientNeeds: PropTypes.array.isRequired,
  fields: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initializeForm: PropTypes.func.isRequired,
  registerField: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  referral: PropTypes.object.isRequired,
  onUpdate: PropTypes.func,
  currentEmployee: PropTypes.object,
  personId: PropTypes.func.isRequired,
};

function mapStateToProps(state, ownProps) {
  const {
    session: {
      groupId,
    },
  } = state;

  return {
    groupId,
    currentEmployee: get(state, 'globalState.currentEmployee'),
    initialValues: {
      client_need: ownProps.need,
    },
  };
}

export default validateReduxForm(
  {
    form: 'editClientNeed',
    fields,
  },
  mapStateToProps,
  { updateServiceCase },
)(ClientNeedEditForm);
